import React, { useState, useEffect, useCallback, useMemo } from "react";
import { ThemeProvider } from "@mui/material";
import theme from "../../theme";
import UserNavbarComponent from "../../components/Navbar/UserNavbarComponent";
import UserCatagoriesNavbar from "../../components/Navbar/UserCatagoriesNavbar";
import UserHeroSectionComponents from "../../components/Hero/UserHeroSectionComponents";
import UserHomeProductCardComponent from "../../components/Card/UserHomeProductCardComponent";
import SkeletonCardComponent from "../../components/Skeleton/SkeletonCardComponent";
import { GetProductAPI } from "../../api/product/ProductController";
import { _fetchMarketingCategoriesService } from "../../service/MasterDataService";
import NoDataComponent from "../../components/NoData/NoDataComponent";

export default function HomePage({ history }) {
  const [selectedMarketingCategory, setSelectedMarketingCategory] = useState("Home");
  const [marketingCategoriesList, setMarketingCategoriesList] = useState([]);
  const [products, setProducts] = useState([]);
  const [isFetching, setIsFetching] = useState(false);
  const normalizedMarketingCategories = useMemo(() => {
    const normalized = marketingCategoriesList
      .map((item) => {
        if (typeof item === "string") {
          return { title: item, key: item };
        }
        return {
          title: item?.title || item?.key || "",
          key: item?.key || item?.title || "",
        };
      })
      .filter((item) => item.title || item.key);

    const seenKeys = new Set();
    return normalized.filter((item) => {
      const key = item.key || item.title;
      if (!key || seenKeys.has(key)) {
        return false;
      }
      seenKeys.add(key);
      return true;
    });
  }, [marketingCategoriesList]);

  const effectiveCategoryItem = useMemo(() => {
    if (selectedMarketingCategory === "Home") {
      return normalizedMarketingCategories[0] || null;
    }
    return (
      normalizedMarketingCategories.find(
        (item) =>
          item.key === selectedMarketingCategory ||
          item.title === selectedMarketingCategory
      ) || null
    );
  }, [selectedMarketingCategory, normalizedMarketingCategories]);

  const effectiveCategoryKey = effectiveCategoryItem?.key || "";
  const effectiveCategoryTitle = effectiveCategoryItem?.title || "";
  const marketingCategoriesPreview = useMemo(
    () => normalizedMarketingCategories,
    [normalizedMarketingCategories]
  );

  // Fetch categories on initial load
  useEffect(() => {
    _fetchMarketingCategoriesService(setMarketingCategoriesList, setIsFetching);
  }, []);

  useEffect(() => {
    if (selectedMarketingCategory === "Home" && normalizedMarketingCategories.length > 0) {
      setSelectedMarketingCategory(normalizedMarketingCategories[0]?.title || "");
    }
  }, [normalizedMarketingCategories, selectedMarketingCategory]);

  // Fetch products based on the selected marketing category
  const fetchProducts = useCallback(async () => {
    setIsFetching(true);
    const fetchCategory =
      effectiveCategoryTitle || effectiveCategoryKey || "New Arrivals";
    const postBody = {
      product_Id: 0,
      Materials: [],
      color: [],
      category: "",
      marketingCategory: fetchCategory,
      MarketingCategory: fetchCategory,
      priceRange: "",
    };
    await GetProductAPI(postBody, setProducts, setIsFetching, true);
  }, [effectiveCategoryKey, effectiveCategoryTitle]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const filteredProducts = useMemo(() => {
    if (!effectiveCategoryKey && !effectiveCategoryTitle) {
      return products;
    }
    const normalizeCategory = (value) =>
      String(value || "").toLowerCase().replace(/\s+/g, "");
    const normalizedTitle = normalizeCategory(effectiveCategoryTitle);
    const normalizedKey = normalizeCategory(effectiveCategoryKey);
    return products.filter((item) => {
      const productCategory =
        item?.marketingCategory ||
        item?.MarketingCategory ||
        item?.marketing_Category ||
        item?.marketingcategory;
      if (!productCategory) {
        return false;
      }
      const normalizedProduct = normalizeCategory(productCategory);
      return (
        (normalizedTitle && normalizedProduct === normalizedTitle) ||
        (normalizedKey && normalizedProduct === normalizedKey)
      );
    });
  }, [effectiveCategoryKey, effectiveCategoryTitle, products]);

  return (
    <ThemeProvider theme={theme}>
      <UserNavbarComponent history={history} />
      <UserHeroSectionComponents
        onShopClick={() => {
          const section = document.getElementById("home-categories");
          if (section) {
            section.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        }}
      />
      <UserCatagoriesNavbar
        setSelectedCategory={(category) => {
          if (!category) {
            return;
          }
          if (typeof category === "string") {
            setSelectedMarketingCategory(category);
            return;
          }
          setSelectedMarketingCategory(category.title || category.key || "");
        }}
        categoriesList={marketingCategoriesPreview}
        initialCategory={marketingCategoriesPreview[0]}
        showViewAll={marketingCategoriesList.length > 0}
        onViewAll={() =>
          history?.push ? history.push("/categories") : window.location.replace("/categories")
        }
      />
      {isFetching ? (
        console.log("Rendering SkeletonCardComponent") || (
          <SkeletonCardComponent />
        )
      ) : filteredProducts.length > 0 ? (
        <UserHomeProductCardComponent
          title=""
          data={filteredProducts}
          history={history}
        />
      ) : (
        <NoDataComponent />
      )}
    </ThemeProvider>
  );
}
